import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { commonImages } from '@images/common-images';
import { DashService } from '@modules/dash-board/dash.service';
import { ApiService } from '@services/api/api.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

@Component({
  selector: 'app-edit-user-images',
  templateUrl: './edit-user-images.component.html',
  styleUrls: ['./edit-user-images.component.scss'],
})
export class EditUserImagesComponent implements OnInit {
  @Input() userId: string;
  @Output() updateUserImages = new EventEmitter<any>();
  S3_url = 'https://remmi-xm.s3.ap-southeast-2.amazonaws.com/';
  images_list: any[] = [];
  isCircle = true;
  uploadProgress: number = 0;
  upload_date = null;
  mobile_device = false;
  common_images = commonImages;

  constructor(
    private Api: ApiService,
    private http: HttpClient,
    private redux: ReduxService,
    private toastr: ToastrService,
    private dash: DashService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  // Lifecycle hook: Called when the component initializes
  ngOnInit() {
    this.getUserDetails(this.userId);
    this.dash.screenSize$.subscribe(size => {
      if (size.width < 630) {
        this.mobile_device = true;
      } else {
        this.mobile_device = false;
      }
    });
  }

  // Adds a new image option to the images list
  addOption() {
    this.images_list.push({
      isChecked: false,
      photoHR: null,
      photoLR: null,
      face_photo: null,
      imageChangedEvent: null,
    });
  }

  // Removes an image option from the list by index
  removeOption(i) {
    const item = this.images_list[i];
    // A blank row has nothing saved yet, so it can go without asking
    if (!item?.photoHR && !item?.photoLR && !item?.face_photo) {
      this.images_list.splice(i, 1);
      this.updateImages();
      return;
    }
    this.remmiConfirm
      .delete({ title: 'Delete Photo?', message: 'This photo set will be removed from the user straight away.' })
      .subscribe(isConfirmed => {
        if (!isConfirmed) return;
        this.images_list.splice(i, 1);
        this.updateImages();
      });
  }

  // Deletes an image based on the provided parameter ('LR' for low-res, otherwise removes face photo)
  deleteImg(i, param) {
    if (param == 'LR') {
      this.images_list[i].photoLR = null;
    } else {
      this.images_list[i].face_photo = null;
      this.images_list[i].imageChangedEvent = null;
    }
  }

  // Handles file selection for high-resolution images
  onSelectFile(event, i) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length) {
      const file = target.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      // Step 1: File type check
      if (!allowedTypes.includes(file.type)) {
        this.toastr.error('Unsupported file format! Please upload a valid image');
        target.value = '';
        return;
      }
      let today = moment();
      this.upload_date = today.format('DD/MM/yy');
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        width >= 1920 && height >= 1080 ? (this.images_list[i].isHR = true) : (this.images_list[i].isHR = false);
        this.images_list[i].imageChangedEvent = event;
        const fileReader: FileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = (e: any) => {
          this.images_list[i].photoHR = e.target.result;
          if (this.images_list?.length == 1 && i == 0) {
            this.images_list[i].isChecked = true;
          }
          this.createLowResolutionImage(e.target.result, i);
        };
      };
      img.onerror = () => {
        this.toastr.error('Corrupted image file! Please upload a valid image');
        target.value = '';
        URL.revokeObjectURL(img.src);
      };
    }
  }

  // Handles file selection for low-resolution images
  onSelectFileLR(event, i) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length) {
      const file = target.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      // Step 1: File type check
      if (!allowedTypes.includes(file.type)) {
        this.toastr.error('Unsupported file format! Please upload a valid image');
        target.value = '';
        return;
      }
      const fileReader: FileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = (e: any) => {
        this.createLowResolutionImage(e.target.result, i);
      };
    }
  }

  // Handles file selection for face photo
  onSelectFileFP(event, i) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length) {
      const file = target.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      // Step 1: File type check
      if (!allowedTypes.includes(file.type)) {
        this.toastr.error('Unsupported file format! Please upload a valid image');
        target.value = '';
        return;
      }
      this.images_list[i].imageChangedEvent = event;
    }
  }

  // Creates a low-resolution version of an image
  createLowResolutionImage(imageDataUrl: string, i) {
    const img = new Image();
    img.src = imageDataUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const width = 200;
      const height = (img.height / img.width) * width;
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      this.images_list[i].photoLR = canvas.toDataURL('image/png');
    };
  }

  // Handles image cropping and updates face photo
  imageCropped(event: any, i) {
    const reader = new FileReader();
    reader.readAsDataURL(event.blob);
    reader.onloadend = () => {
      this.images_list[i].face_photo = reader.result as string;
    };
  }

  // Updates the default selected photo
  updateDefaultPhoto(ind) {
    this.images_list.forEach(item => {
      item.isChecked = false;
    });
    this.images_list[ind].isChecked = true;
    this.updateImages();
  }

  // Uploads images to the server
  updateImages() {
    console.log('progress >>:', this.uploadProgress);
    const filteredImages = this.images_list?.filter(img => img?.photoHR !== null) || [];

    const payload = {
      user_images: filteredImages,
      upload_date: new Date(),
    };

    // const formData = new FormData();
    // formData.append('upload_date', new Date().toISOString());
    // formData.append('user_images', JSON.parse(filteredImages));
    // const req = new HttpRequest(
    //   'POST', `${this.Api.baseUrl}/users/update-images/${this.userId}`, formData,
    //   { reportProgress: true }
    // );
    //    this.http.request(req)

    this.http
      .post(`${this.Api.baseUrl}/users/update-images/${this.userId}`, payload, {
        reportProgress: true,
        observe: 'events',
      })
      .subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round((100 * event.loaded) / (event.total ?? 1));
            console.log('progress >>:', this.uploadProgress);
            // this.uploadProgress = Math.round(100 * (event.loaded / event.total));
          } else if (event.type === HttpEventType.Response) {
            if (this.uploadProgress == 100) {
              if (this.images_list?.length > 0) {
                this.toastr.success('Images upload successfully');
              }
              this.uploadProgress = 0;
              this.getUserDetails(this.userId, true);
              if (this.userId === this.Api.getUserId()) {
                this.redux.setUser(null);
                this.redux.setMyOffices(null);
              }
            }
          }
        },
        error: err => {
          console.error('Upload error:', err);
          this.toastr.error('Failed to upload images');
          this.uploadProgress = 0;
        },
      });
  }

  // Fetches user details and images from the API
  getUserDetails(user_id, isEmit = false) {
    this.Api.get('/users/' + user_id).subscribe({
      next: response => {
        if (response?.data?.user_images?.length > 0) {
          this.images_list = response?.data?.user_images?.map(rex => {
            rex = { face_photo: rex?.face_photo, photoHR: rex?.photoHR, imageChangedEvent: null, photoLR: rex?.photoLR, isChecked: rex?.isChecked };
            return rex;
          });
        }
        this.upload_date = moment(response?.data?.upload_date).format('DD/MM/yy');
        if (isEmit == true) {
          var images = { croppedImage: response?.data?.face_photo, photo_hr: response?.data?.photo_high_resolution, photo: response?.data?.photo };
          this.updateUserImages.emit(images);
        }
      },
    });
  }
}
